/* eslint-disable max-len */

import networkIsolatedIcon from 'resources/images/deployment-model-network-isolated.svg';
import externalAccessIcon from 'resources/images/deployment-model-external-access.svg';
import externalProcessingIcon from 'resources/images/deployment-model-external-processing.svg';
import humanReviewIcon from 'resources/images/deployment-model-human-review.svg';
import segmentIcon from 'resources/images/icon-segment.svg';
import streamIcon from 'resources/images/icon-stream.svg';
import batchIcon from 'resources/images/icon-batch.svg';

export const MAX_NUMBER_OF_INDUSTRY = 3;

export const engineModes = {
  segment: {
    title: 'Segment',
    icon: segmentIcon,
    description: `In Segment mode, an engine will process data in
    small, discrete chunks, which arise from segmentation of the input data into messages.
    The engine pulls chunks from a stateless queue, which means the engine can process each
    discrete bit of content without having any knowledge of
    the content that came before or after the current chunk.`,
    value: 'chunk',
    displayInCreateNewEngine: true,
    link: 'https://docs.veritone.com/#/applications/aiware-applications/developer/processing-modes',
  },
  stream: {
    title: 'Stream',
    icon: streamIcon,
    description: `Stream-based engines process data in streams,
    using a stateful queue, where the order of the data does matter.
    Because they store state, stream engines can use results from earlier cognition
    to inform later stages of processing.
    Generally, the engine needs to take care of decoding the raw input itself.`,
    value: 'stream',
    displayInCreateNewEngine: true,
    link: 'https://docs.veritone.com/#/applications/aiware-applications/developer/processing-modes',
  },
  batch: {
    title: 'Batch',
    icon: batchIcon,
    description: `Batch engines are designed to process an entire
    file in one task (process) invocation and communicate directly with
    the Veritone aiWARE GraphQL API. Because Batch engines simply execute all
    of the logic defined in the engine this engine mode is ideal for handling
    business logic like app integrations and other utility use-cases.`,
    value: 'batch',
    displayInCreateNewEngine: true,
    link: '',
  },
};

export const createEngineHelpLink =
  'https://docs.veritone.com/#/developer/engines/toolkit/?id=introduction';
export const engineClassHelpLink =
  'https://docs.veritone.com/#/developer/engines/cognitive/classes/';
export const engineModeHelpLink =
  'https://docs.veritone.com/#/applications/aiware-applications/developer/processing-modes';
export const supportedInputTypesHelpLink =
  'https://docs.veritone.com/#/developer/engines/engine-input.md';
export const customFieldsHelpLink =
  'https://docs.veritone.com/#/developer/engines/custom-fields/';
export const deploymentModelHelpLink =
  'https://docs.veritone.com/#/developer/engines/deployment-model/';
export const networkIsolatedModelHelpLink =
  'https://docs.veritone.com/#/developer/engines/deployment-model/?id=network-isolated';
export const externalAccessModelHelpLink =
  ' https://docs.veritone.com/#/developer/engines/deployment-model/?id=external-access';
export const externalProcessingModelHelpLink =
  'https://docs.veritone.com/#/developer/engines/deployment-model/?id=external-processing';
export const humanReviewModelHelpLink =
  'https://docs.veritone.com/#/developer/engines/deployment-model/?id=human-review';
export const libraryHelpLink =
  'https://docs.veritone.com/#/developer/libraries/engines';
export const engineCapabilityLink = (engineClass, capability) =>
  `https://docs.veritone.com/#/developer/engines/cognitive/${engineClass}/${capability}/`;

export const engineCapabilityLinkMap = {
  Fingerprint:
    'https://docs.veritone.com/#/developer/engines/cognitive/audio/audio-fingerprinting/',
  'Audio Detection':
    'https://docs.veritone.com/#/developer/engines/cognitive/speech/speaker-detection/',
  'Facial Detection':
    'https://docs.veritone.com/#/developer/engines/cognitive/biometrics/face-detection/',
  Translate:
    'https://docs.veritone.com/#/developer/engines/cognitive/text/translation/',
  'Text to Speech':
    'https://docs.veritone.com/#/developer/engines/cognitive/text/translation/transcript/',
  'Logo Recognition':
    'https://docs.veritone.com/#/developer/engines/cognitive/vision/logo-detection/',
  'License Plate (ALPR)':
    'https://docs.veritone.com/#/developer/engines/cognitive/vision/license-plate/',
  Automation:
    'https://docs.veritone.com/#/developer/engines/cognitive/capabilities/',
  Transcode:
    'https://docs.veritone.com/#/developer/engines/cognitive/capabilities/',
};

export const listDeploymentModel = {
  FullyNetworkIsolated: {
    title: 'Network Isolated',
    subtitle: `The engine does all processing entirely within its container (ie. itself),
    running solely within Veritone's infrastructure. There is no internet access,
    and no external calls are made to the outside world ever.
    This is the most secure deployment model, which makes "network-isolated deployment"
    a requirement for most Government agencies and Legal & Compliance customers.
    This deployment is also highly requested by Media & Entertainment customers,
    since network-isolated engines may be deployed on-premises.`,
    value: 'FullyNetworkIsolated',
    icon: networkIsolatedIcon,
    helpInfoLink: networkIsolatedModelHelpLink,
    shortName: 'NI',
  },
  MostlyNetworkIsolated: {
    title: 'External Access',
    subtitle: `The engine requires internet access for tasks like license checks to an
    external server, or downloading reference data,
    but does not send user data outside the container.`,
    value: 'MostlyNetworkIsolated',
    icon: externalAccessIcon,
    helpInfoLink: externalAccessModelHelpLink,
    shortName: 'EA',
  },
  NonNetworkIsolated: {
    title: 'External Processing',
    subtitle: `The engine does some or all of its processing outside its container
    and therefore requires internet access. For example,
    user data may need to be sent to external services (such as a cloud API) for processing.`,
    value: 'NonNetworkIsolated',
    icon: externalProcessingIcon,
    helpInfoLink: externalProcessingModelHelpLink,
    shortName: 'EP',
  },
  HumanReview: {
    title: 'Human Review',
    subtitle: `The engine requires human intervention to complete its processing,
    and therefore needs network access.`,
    value: 'HumanReview',
    icon: humanReviewIcon,
    helpInfoLink: humanReviewModelHelpLink,
    shortName: 'HR',
  },
};

export const GENERATE_MANIFEST = 'GENERATE_MANIFEST';

export const listIndustries = [
  'Government',
  'Media and entertainment',
  'Legal and compliance',
  'Other',
];

export const listMediaFiles = [
  {
    class: 'speech',
    category: [
      {
        name: 'speaker_detection',
        mediaFileUris: [
          {
            name: 'Speaker Detection',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/voice/voice_recognition_test.wav',
          },
        ],
      },
      {
        name: 'transcription',
        mediaFileUris: [
          {
            name: 'Afrikaans',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Afrikaans_udhr.mp3',
          },
          {
            name: 'Arabic (Saudi Arabia)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Arabic_udrh.mp3',
          },
          {
            name: 'Bulgarian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Bulgarian_udhr.mp3',
          },
          {
            name: 'Catalan',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Catalan_udhr.mp3',
          },
          {
            name: 'Malay',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Malaysian_Malay_udhr.mp3',
          },
          {
            name: 'Chinese (Cantonese)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Cantonese_Chinese_udhr.mp3',
          },
          {
            name: 'Croatian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Croatian_udhr.mp3',
          },
          {
            name: 'Czech',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Czech_udhr.mp3',
          },
          {
            name: 'Danish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Danish_udhr.mp3',
          },
          {
            name: 'Dutch',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Dutch_udhr.mp3',
          },
          {
            name: 'English (Australia)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/English_Australia_udhr.mp3',
          },
          {
            name: 'English (Canada)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/English_Canada_udhr.mp3',
          },
          {
            name: 'English (UK)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/English_UK_udhr.mp3',
          },
          {
            name: 'English (US)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/English_udhr.mp3',
          },
          {
            name: 'Finnish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Finnish_udhr.mp3',
          },
          {
            name: 'French (Canada)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/French_canada_udhr.mp3',
          },
          {
            name: 'French (France)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/French_udhr.mp3',
          },
          {
            name: 'German',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/German_udhr.mp3',
          },
          {
            name: 'Greek',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Greek_udg.mp3',
          },
          {
            name: 'Hebrew',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Hebrew_udhr.mp3',
          },
          {
            name: 'Hindi',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Hindi_udhr.mp3',
          },
          {
            name: 'Hungarian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Hungarian_udhr.mp3',
          },
          {
            name: 'Italian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Italian_udhr.mp3',
          },
          {
            name: 'Japanese',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Japanese_udhr.mp3',
          },
          {
            name: 'Korean',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Koeran_udhr.mp3',
          },
          {
            name: 'Latvian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Latvian_udhr.mp3',
          },
          {
            name: 'Lithuanian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Lithuanian_udhr.mp3',
          },
          {
            name: 'Malaysian (Malay)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Malaysian_Malay_udhr.mp3',
          },
          {
            name: 'Norwegian (Bokmal)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Norwegian_Bokmal_udhr.mp3',
          },
          {
            name: 'Persian (Farsi)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Persian_Farsi_udhr.mp3',
          },
          {
            name: 'Polish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Polish_udhr.mp3',
          },
          {
            name: 'Portuguese (Portugal)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Portuguese_udhr.mp3',
          },
          {
            name: 'Romanian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Romania_udhr.mp3',
          },
          {
            name: 'Russian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Russian_udhr.mp3',
          },
          {
            name: 'Serbian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Serbian_udhr.mp3',
          },
          {
            name: 'Shanghainese (Chinese)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Shanghainese_Chinese.mp3',
          },
          {
            name: 'Slovak',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Slovak_udhr.mp3',
          },
          {
            name: 'Slovenian (Broadcast News)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Slovenian_News.mp3',
          },
          {
            name: 'Spanish (Mexico)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Spanish_Mexico_udhr.mp3',
          },
          {
            name: 'Swedish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Swedish_udhr.mp3',
          },
          {
            name: 'Thai',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Thai_udhr.mp3',
          },
          {
            name: 'Turkish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Turkish_udhr.mp3',
          },
          {
            name: 'Ukranian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Ukranian_udhr.mp3',
          },
          {
            name: 'Welsh',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/transcription/Welsh_udhr.mp3',
          },
        ],
      },
    ],
  },
  {
    class: 'text',
    category: [
      {
        name: 'translate',
        mediaFileUris: [
          {
            name: 'Afrikaans',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/afrikaans.txt',
          },
          {
            name: 'Arabic',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Arabic00001.txt',
          },
          {
            name: 'Bulgarian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/bulgarian.txt',
          },
          {
            name: 'Catalan',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/catalan.txt',
          },
          {
            name: 'Chinese (Cantonese)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Chinese_Simplified.txt',
          },
          {
            name: 'Chinese (Mandarin)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Chinese_Traditional.txt',
          },
          {
            name: 'Croatian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/croatian.txt',
          },
          {
            name: 'Czech',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/czech.txt',
          },
          {
            name: 'Danish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/danish.txt',
          },
          {
            name: 'Dutch',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/dutch.txt',
          },
          {
            name: 'English',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/english.txt',
          },
          {
            name: 'Finnish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/finnish.txt',
          },
          {
            name: 'French',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/french.txt',
          },
          {
            name: 'German',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/german.txt',
          },
          {
            name: 'Greek',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/greek.txt',
          },
          {
            name: 'Hebrew',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/hebrew.txt',
          },
          {
            name: 'Hindi',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/hindi.txt',
          },
          {
            name: 'Hungarian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/hungarian.txt',
          },
          {
            name: 'Italian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Italian_00001.txt',
          },
          {
            name: 'Japanese',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Japanese_00001.txt',
          },
          {
            name: 'Korean',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Korean_00001.txt',
          },
          {
            name: 'Latvian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/latvian.txt',
          },
          {
            name: 'Lithuanian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/lithuanian.txt',
          },
          {
            name: 'Malaysian (Malay)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/malay.txt',
          },
          {
            name: 'Norwegian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/norwegian.txt',
          },
          {
            name: 'Persian (Farsi)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/persian.txt',
          },
          {
            name: 'Polish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/polish.txt',
          },
          {
            name: 'Portuguese',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Portuguese_00001.txt',
          },
          {
            name: 'Romanian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Romanian_00001.txt',
          },
          {
            name: 'Russian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Russian_00001.txt',
          },
          {
            name: 'Serbian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/serbian.txt',
          },
          {
            name: 'Shanghainese (Chinese)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/shanghainese.txt',
          },
          {
            name: 'Slovak',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Slovakian_00001.txt',
          },
          {
            name: 'Slovenian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/slovenian.txt',
          },
          {
            name: 'Spanish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Spanish_00001.txt',
          },
          {
            name: 'Swedish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/Swedish_00001.txt',
          },
          {
            name: 'Thai',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/thai.txt',
          },
          {
            name: 'Turkish',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/turkish.txt',
          },
          {
            name: 'Ukranian',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/ukranian.txt',
          },
          {
            name: 'Welsh',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/welsh.txt',
          },
        ],
      },
      {
        name: 'text_to_speech',
        mediaFileUris: [
          {
            name: 'Text to Speech',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/translation/english.txt',
          },
        ],
      },
      {
        name: 'entity_extraction',
        mediaFileUris: [
          {
            name: 'Entity Extraction',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'content_classification',
        mediaFileUris: [
          {
            name: 'Content Classification',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'keyword_extraction',
        mediaFileUris: [
          {
            name: 'Keyword Extraction',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'language_identification',
        mediaFileUris: [
          {
            name: 'Language Identification',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'summarization',
        mediaFileUris: [
          {
            name: 'Summary',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'sentiment',
        mediaFileUris: [
          {
            name: 'Sentiment',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'text_extraction',
        mediaFileUris: [
          {
            name: 'Text Extraction',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
    ],
  },
  {
    class: 'audio',
    category: [
      {
        name: 'audio_detection',
        mediaFileUris: [
          {
            name: 'Audio Detection',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/audio/audio_detection_test.wav',
          },
        ],
      },
      {
        name: 'fingerprint',
        mediaFileUris: [
          {
            name: 'Fingerprint',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/audio/audio_detection_test.wav',
          },
        ],
      },
    ],
  },
  {
    class: 'transformation',
    category: [
      {
        name: 'transcode',
        mediaFileUris: [
          {
            name: 'Transcode',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
    ],
  },
  {
    class: 'data',
    category: [
      {
        name: 'geolocation',
        mediaFileUris: [
          {
            name: 'Geolocation',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'correlation',
        mediaFileUris: [
          {
            name: 'Correlation',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
      {
        name: 'automation',
        mediaFileUris: [
          {
            name: 'Automation',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/text_analytics/cbc_news.txt',
          },
        ],
      },
    ],
  },
  {
    class: 'biometrics',
    category: [
      {
        name: 'facial_detection',
        mediaFileUris: [
          {
            name: 'Face Recognition',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/face/face_rec_test.jpg',
          },
        ],
      },
    ],
  },
  {
    class: 'vision',
    category: [
      {
        name: 'license_plate_(alpr)',
        mediaFileUris: [
          {
            name: 'License Plate (ALPR)',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/license_plate/license_plate_test.jpg',
          },
        ],
      },
      {
        name: 'logo_recognition',
        mediaFileUris: [
          {
            name: 'Logo Recognition',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/logo/logo_detection_test.jpg',
          },
        ],
      },
      {
        name: 'object_detection',
        mediaFileUris: [
          {
            name: 'Object Detection',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/object/obj_detection_test.jpg',
          },
        ],
      },
      {
        name: 'text_recognition',
        mediaFileUris: [
          {
            name: 'Text Recognition',
            value:
              'https://veritone-docs-prod.s3.amazonaws.com/test-data/ocr/cbc_news.pdf',
          },
        ],
      },
    ],
  },
];



// WEBPACK FOOTER //
// ./src/client/modules/engine/constants.js